import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, CheckCircle2 } from "lucide-react";

const STATUS = { consumer_selected: "selected by you (not verified)", self_reported_unmatched: "self-reported, not matched to a plan ID", unknown: "not sure / skipped",
  self_reported_from_card: "tagged from a plan card — current-year plan not confirmed" };

export const CaseCurrentPlan = ({ base, current, hint, hasCounty, onCase, agent }) => {
  const [q, setQ] = useState(hint || "");
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(!current);
  useEffect(() => { if (current?.at) setEditing(false); }, [current?.at]);
  const [open, setOpen] = useState(!!hint);
  const hinted = useRef(false);
  const [busy, setBusy] = useState(false);
  const [statewide, setStatewide] = useState(false);
  const save = async (body) => {
    setBusy(true);
    try { onCase((await api.post(`${base}/current-plan`, body)).data); setEditing(false); }
    catch (e) {
      if (e?.response?.status === 409 && window.confirm(`${formatApiError(e.response.data.detail)}\n\nReplace it?`)) {
        try { onCase((await api.post(`${base}/current-plan`, { ...body, replace: true })).data); setEditing(false); } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail)); }
      } else toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save that plan.");
    }
    setBusy(false);
  };
  const search = async () => {
    try { const d = (await api.get(`${base}/current-plan/search`, { params: { q } })).data; setRows(d.results); setStatewide(d.county_confirmed === false); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => {
    if (!hint || current || hinted.current || !hasCounty) return;
    hinted.current = true;
    setOpen(true); setQ(hint);
    api.get(`${base}/current-plan/search`, { params: { q: hint } }).then((r) => { setRows(r.data.results); setStatewide(r.data.county_confirmed === false); }).catch(() => {});
  }, [hint, current, hasCounty, base]);
  const label = agent ? "Client's current plan (2026)" : "Your current plan (2026)";
  if (!current && !open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="flex min-h-11 w-full items-center gap-2 rounded-xl border border-dashed border-[#BCCCDC] bg-white px-4 text-left text-sm text-[#1E3E62]" data-testid="case-current-plan-open">
        <Search className="h-4 w-4" />{agent ? "Add the client's current 2026 plan (optional)" : "Have a plan now? Add your current 2026 plan (optional)"}
      </button>
    );
  }
  if (!editing && current) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#E5E5E0] bg-white p-4 text-sm" data-testid="case-current-plan">
        <CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" /><span className="font-semibold text-[#0B192C]">{label}:</span>
        <span data-testid="case-current-plan-value">{current.plan_name || current.name || "Not sure"}{current.display_id ? ` · ${current.display_id}` : ""}{current.card_display_id ? ` · shown as ${current.card_display_id} (${current.card_plan_year})` : ""} — {STATUS[current.status] || current.status}</span>
        <button type="button" onClick={() => setEditing(true)} className="ml-auto underline text-[#1E3E62]" data-testid="case-current-plan-change">Change</button>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-[#E5E5E0] bg-white p-4" data-testid="case-current-plan-picker">
      <p className="text-sm font-semibold text-[#0B192C]">{label} — optional</p>
      {hint && !current && <p className="mt-1 text-sm text-[#33475B]" data-testid="case-current-plan-hint">You mentioned {hint}. Which {hint} plan {agent ? "is the client" : "are you"} on now? Pick it below, or skip.</p>}
      {!hasCounty ? <p className="mt-1 text-sm text-[#5A6B7B]">Share your ZIP first and we'll look up 2026 plans in your county.</p> : (
        <>
          <div className="mt-2 flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Carrier, plan name, or plan ID (e.g. H1036-143)" data-testid="case-current-plan-search" />
            <Button variant="outline" onClick={search} disabled={busy} data-testid="case-current-plan-search-btn"><Search className="h-4 w-4" /></Button>
          </div>
          {rows && statewide && rows.length > 0 && <p className="mt-2 text-xs text-[#7A5A12]" data-testid="case-current-plan-statewide">Showing Florida 2026 plans. We couldn't confirm which ones serve your county, so pick the exact plan on your member card.</p>}
          {rows && (
            <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
              {rows.map((r) => (
                <li key={r.display_id}><button type="button" disabled={busy} onClick={() => save({ display_id: r.display_id })} className="w-full rounded-lg px-2 py-2 text-left text-sm hover:bg-[#F4F1E8]" data-testid={`case-current-plan-option-${r.display_id}`}>{r.plan_name} <span className="text-[#5A6B7B]">· {r.carrier} · {r.display_id}</span></button></li>
              ))}
              {!rows.length && <li className="text-sm text-[#5A6B7B]">No 2026 match in your county.</li>}
            </ul>
          )}
        </>
      )}
      <div className="mt-2 flex flex-wrap gap-3 text-sm">
        {q.trim() && <button type="button" disabled={busy} onClick={() => save({ self_reported_name: q.trim() })} className="underline text-[#1E3E62]" data-testid="case-current-plan-self-report">Save "{q.trim()}" as I typed it</button>}
        <button type="button" disabled={busy} onClick={() => save({ skip: true })} className="underline text-[#5A6B7B]" data-testid="case-current-plan-skip">I don't know / skip</button>
      </div>
    </div>
  );
};
